import React, { useEffect, useRef, useState } from 'react';
import { FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaRedo } from 'react-icons/fa';
import { mensagemDeErro, onboardingService, pareceMensagemTecnica, VeiculoPrevia } from '../../services/onboarding';
import { rastrearTeste, segundosDesde } from '../../services/rastreamentoTeste';
import Placa from './Placa';
import { Aguardando, Aviso, Botao, CampoPlaca, CartaoVeiculo, Etiqueta, FaixaSucesso, GradeVeiculos, Rodape, Subtitulo, Titulo } from './styles';

interface Props {
    aoConcluir: () => void;
}

const TIPOS = [
    { valor: 1, rotulo: 'Carro' },
    { valor: 2, rotulo: 'Moto' },
    { valor: 4, rotulo: 'Caminhão' },
];

// Máscara de placa: antiga (ABC-1234) ou Mercosul (ABC-1D23). 3 letras, 1 número, letra ou número,
// 2 números; o hífen entra sozinho. Caractere fora do lugar é ignorado.
const REGRAS_PLACA = [/[A-Z]/, /[A-Z]/, /[A-Z]/, /[0-9]/, /[A-Z0-9]/, /[0-9]/, /[0-9]/];
export const mascararPlaca = (valor: string) => {
    const aceitos: string[] = [];
    for (const c of valor.toUpperCase().replace(/[^A-Z0-9]/g, '')) {
        if (aceitos.length === REGRAS_PLACA.length) break;
        if (REGRAS_PLACA[aceitos.length].test(c)) aceitos.push(c);
    }
    const texto = aceitos.join('');
    return texto.length > 3 ? `${texto.slice(0, 3)}-${texto.slice(3)}` : texto;
};
const placaCompleta = (valor: string) => valor.replace('-', '').length === REGRAS_PLACA.length;

const formatarFipe = (valor?: number) => (valor ? valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }) : '');

const ANO_MINIMO = 1950;
const anoMaximo = () => new Date().getFullYear() + 1;
const anoValido = (ano?: string) => /^\d{4}$/.test(ano ?? '') && Number(ano) >= ANO_MINIMO && Number(ano) <= anoMaximo();

const MOTIVO_NAO_ACHOU = 'Não achamos os dados desta placa. Preencha marca, modelo e ano abaixo para cadastrar.';
const MOTIVO_FALHOU = 'A consulta da placa não respondeu agora. Preencha os dados do veículo abaixo ou tente de novo.';
const MOTIVO_LIMITE = 'Já consultamos todas as placas disponíveis por agora. Preencha os dados do veículo abaixo.';

// Uma placa só: quando ela fica completa, a busca começa sozinha, e o único botão ("Continuar",
// que cadastra e segue) só libera com o veículo encontrado ou preenchido. Qualquer busca que não
// traga o veículo abre o formulário à mão com o motivo, para a pessoa nunca ficar parada aqui.
// Os demais veículos o locador cadastra depois, pela tela de Veículos.
export default function EtapaFrota({ aoConcluir }: Props) {
    const [placa, setPlaca] = useState('');
    const [veiculo, setVeiculo] = useState<VeiculoPrevia | null>(null);
    const [buscando, setBuscando] = useState(false);
    const [cadastrando, setCadastrando] = useState(false);
    const [demorando, setDemorando] = useState(false);
    // Por que o formulário à mão apareceu, e se vale a pena consultar a placa de novo.
    const [motivo, setMotivo] = useState('');
    const [podeTentarDeNovo, setPodeTentarDeNovo] = useState(false);
    const [erroCadastro, setErroCadastro] = useState('');

    // A consulta de placa (serviço externo) às vezes demora: depois de alguns segundos, oferece
    // seguir preenchendo à mão em vez de deixar a pessoa esperando.
    useEffect(() => {
        if (!buscando) { setDemorando(false); return; }
        const aviso = window.setTimeout(() => setDemorando(true), 6000);
        return () => window.clearTimeout(aviso);
    }, [buscando]);

    // Placa da busca em andamento: se a pessoa mudar a placa antes da resposta, a resposta velha é descartada.
    const placaBuscada = useRef('');

    type MotivoManual = 'demora' | 'falha' | 'nao_encontrada' | 'limite';
    const abrirManual = (placaDoVeiculo: string, texto: string, tentarDeNovo: boolean, motivoManual: MotivoManual, base?: Partial<VeiculoPrevia>, extra?: Record<string, unknown>) => {
        rastrearTeste('onboarding_placa_manual', { motivo: motivoManual, ...extra });
        setVeiculo({
            placa: placaDoVeiculo.replace('-', ''), encontrado: false, jaCadastrado: false, foraDoLimite: false, tipoVeiculo: 1, marca: '', modelo: '', ano: '',
            ...base,
            // O ano da busca pode vir como "2020/2021": no formulário só cabem 4 números.
            ...(base?.ano ? { ano: String(base.ano).replace(/\D/g, '').slice(0, 4) } : {}),
        });
        setMotivo(texto);
        setPodeTentarDeNovo(tentarDeNovo);
    };

    const preencherAMao = () => {
        placaBuscada.current = '';
        setBuscando(false);
        abrirManual(placa, 'Preencha os dados do veículo.', true, 'demora');
    };

    const buscar = async (alvo: string) => {
        const inicio = Date.now();
        const registrarBusca = (resultado: 'encontrada' | 'nao_encontrada' | 'falha' | 'limite' | 'ja_cadastrada', extra?: Record<string, unknown>) =>
            rastrearTeste('onboarding_placa_buscada', { resultado, segundos: segundosDesde(inicio), ...extra });
        placaBuscada.current = alvo;
        setBuscando(true);
        setVeiculo(null);
        setMotivo('');
        setErroCadastro('');
        try {
            const resultado = await onboardingService.interpretarPlacas(alvo);
            if (placaBuscada.current !== alvo) return;
            const achado = resultado?.veiculos?.[0];
            if (!achado) {
                registrarBusca('nao_encontrada');
                abrirManual(alvo, MOTIVO_NAO_ACHOU, true, 'nao_encontrada');
                return;
            }
            if (achado.foraDoLimite) {
                registrarBusca('limite');
                abrirManual(achado.placa || alvo, MOTIVO_LIMITE, false, 'limite');
                return;
            }
            setPlaca(mascararPlaca(achado.placa || alvo));
            if (achado.jaCadastrado) {
                registrarBusca('ja_cadastrada');
                setVeiculo(achado);
                return;
            }
            // Achou, mas sem marca, modelo ou ano: a pessoa completa à mão o que faltou.
            const dadosCompletos = Boolean(achado.marca?.trim() && achado.modelo?.trim() && anoValido(achado.ano));
            if (!achado.encontrado || !dadosCompletos) {
                // Achou com dados faltando: conta como encontrada, mas o formulário à mão abre.
                registrarBusca(achado.encontrado ? 'encontrada' : 'nao_encontrada', achado.encontrado ? { dadosIncompletos: true } : undefined);
                abrirManual(achado.placa || alvo, achado.encontrado ? 'Achamos a placa, mas faltam alguns dados. Complete abaixo para cadastrar.' : MOTIVO_NAO_ACHOU, true, 'nao_encontrada', {
                    tipoVeiculo: achado.tipoVeiculo || 1, marca: achado.marca ?? '', modelo: achado.modelo ?? '', ano: achado.ano ?? '', cor: achado.cor, chassi: achado.chassi, valorFipe: achado.valorFipe,
                }, achado.encontrado ? { dadosIncompletos: true } : undefined);
                return;
            }
            registrarBusca('encontrada');
            setVeiculo(achado);
        } catch (error) {
            if (placaBuscada.current === alvo) {
                registrarBusca('falha');
                abrirManual(alvo, mensagemDeErro(error, MOTIVO_FALHOU), true, 'falha');
            }
        } finally {
            if (placaBuscada.current === alvo) setBuscando(false);
        }
    };

    const trocarPlaca = (valor: string) => {
        const nova = mascararPlaca(valor);
        if (nova === placa) return;
        setPlaca(nova);
        setVeiculo(null);
        setMotivo('');
        setErroCadastro('');
        if (placaCompleta(nova)) buscar(nova);
        else { placaBuscada.current = ''; setBuscando(false); }
    };

    const alterar = (campo: keyof VeiculoPrevia, valor: string | number) =>
        setVeiculo(atual => (atual ? { ...atual, [campo]: valor } : atual));

    const manual = Boolean(veiculo && !veiculo.jaCadastrado && !veiculo.encontrado);
    const anoPreenchido = Boolean(veiculo?.ano?.trim());
    const anoOk = !manual || anoValido(veiculo?.ano);
    const completo = Boolean(veiculo?.marca?.trim() && veiculo?.modelo?.trim() && anoPreenchido && anoOk);
    const podeContinuar = Boolean(veiculo && !buscando && !cadastrando && (veiculo.jaCadastrado || completo));

    const continuar = async () => {
        if (!veiculo || !podeContinuar) return;
        if (veiculo.jaCadastrado) {
            aoConcluir();
            return;
        }
        setCadastrando(true);
        setErroCadastro('');
        try {
            const resultado = await onboardingService.cadastrarVeiculos([veiculo]);
            if (resultado.cadastrados > 0) {
                rastrearTeste('onboarding_veiculo_cadastrado', { origem: veiculo.encontrado ? 'busca' : 'manual' });
                aoConcluir();
                return;
            }
            const motivoDoServidor = (resultado.erros ?? []).find(e => !pareceMensagemTecnica(e));
            setErroCadastro(motivoDoServidor || 'Não foi possível cadastrar o veículo. Confira os dados e tente de novo.');
        } catch (error) {
            setErroCadastro(mensagemDeErro(error, 'Não foi possível cadastrar o veículo agora. Tente de novo em instantes.'));
        }
        setCadastrando(false);
    };

    const aoTeclar = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        if (podeContinuar) { continuar(); return; }
        // Enter na mesma placa refaz a busca (depois de uma falha, por exemplo).
        if (!buscando && placaCompleta(placa) && (!veiculo || (manual && podeTentarDeNovo))) buscar(placa);
    };

    return (
        <>
            <Titulo>Cadastre a sua frota</Titulo>
            <Subtitulo>Digite a placa de um veículo seu. Buscamos marca, modelo, ano e valor FIPE. Os outros você cadastra depois.</Subtitulo>

            <CampoPlaca
                value={placa}
                onChange={e => trocarPlaca(e.target.value)}
                onKeyDown={aoTeclar}
                placeholder="ABC-1D23"
                maxLength={8}
                autoFocus
            />
            {buscando && <Aguardando style={{ marginTop: 10 }}>Buscando a placa...</Aguardando>}
            {buscando && demorando && (
                <Aviso style={{ marginTop: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <span>A consulta da placa está demorando. Se preferir, preencha os dados do veículo à mão.</span>
                    <Botao type="button" $variante="secundario" onClick={preencherAMao}>Preencher à mão</Botao>
                </Aviso>
            )}

            {/* Retorno claro da busca: verde quando achou, amarelo quando falta completar. */}
            {veiculo && !veiculo.jaCadastrado && veiculo.encontrado && (
                <FaixaSucesso style={{ marginTop: 18, marginBottom: 0 }}>
                    <FaCheckCircle />
                    <div>
                        <h1>Veículo encontrado</h1>
                        <p>{[veiculo.marca, veiculo.modelo, veiculo.ano].filter(Boolean).join(' ')}. Confira os dados e clique em Continuar para cadastrar.</p>
                    </div>
                </FaixaSucesso>
            )}
            {manual && (
                <Aviso $tipo="erro" style={{ marginTop: 18, background: '#fff8e1', borderColor: '#f5d27a', color: '#8a5a00', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FaExclamationTriangle style={{ color: '#d39e00' }} />
                    <span style={{ flex: 1, minWidth: 220 }}>{motivo || MOTIVO_NAO_ACHOU}</span>
                    {podeTentarDeNovo && placaCompleta(placa) && (
                        <Botao type="button" $variante="secundario" onClick={() => buscar(placa)}><FaRedo style={{ color: 'inherit' }} /> Tentar de novo</Botao>
                    )}
                </Aviso>
            )}
            {veiculo?.jaCadastrado && (
                <Aviso style={{ marginTop: 18 }}><FaInfoCircle /> Este veículo já está na sua frota. Clique em Continuar.</Aviso>
            )}

            {veiculo && (
                <GradeVeiculos>
                    <CartaoVeiculo $estado={veiculo.jaCadastrado ? 'inativo' : completo ? 'ok' : 'pendente'}>
                        <div className="linha"><Placa placa={veiculo.placa} /></div>

                        {!veiculo.jaCadastrado && veiculo.encontrado && (
                            <>
                                <div className="nome">{veiculo.marca} {veiculo.modelo}</div>
                                <div className="detalhe">
                                    {[veiculo.ano, TIPOS.find(t => t.valor === veiculo.tipoVeiculo)?.rotulo ?? 'Carro', veiculo.cor].filter(Boolean).join(' · ')}
                                </div>
                                <div className="linha">
                                    <span className="detalhe">{veiculo.chassi ? <>Chassi <span className="chassi">{veiculo.chassi}</span></> : 'Chassi não informado'}</span>
                                    {veiculo.valorFipe ? <span className="fipe">FIPE {formatarFipe(veiculo.valorFipe)}</span> : null}
                                </div>
                            </>
                        )}

                        {manual && (
                            <>
                                <Etiqueta $cor="amarelo">Preencha os dados do veículo</Etiqueta>
                                <div className="campos">
                                    <select value={veiculo.tipoVeiculo || 1} onChange={e => alterar('tipoVeiculo', Number(e.target.value))}>
                                        {TIPOS.map(t => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
                                    </select>
                                    <input placeholder="Ano" inputMode="numeric" maxLength={4} value={veiculo.ano ?? ''} onChange={e => alterar('ano', e.target.value.replace(/\D/g, '').slice(0, 4))} />
                                    <input placeholder="Marca" value={veiculo.marca ?? ''} onChange={e => alterar('marca', e.target.value)} />
                                    <input placeholder="Modelo" value={veiculo.modelo ?? ''} onChange={e => alterar('modelo', e.target.value)} />
                                    <input className="inteiro" placeholder="Chassi (opcional)" value={veiculo.chassi ?? ''} onChange={e => alterar('chassi', e.target.value.toUpperCase())} />
                                </div>
                                {anoPreenchido && !anoOk && (
                                    <span className="detalhe" style={{ color: '#842029' }}>Informe o ano com 4 números, entre {ANO_MINIMO} e {anoMaximo()}.</span>
                                )}
                            </>
                        )}

                        {veiculo.jaCadastrado && <Etiqueta $cor="cinza">Já está na sua frota</Etiqueta>}
                    </CartaoVeiculo>
                </GradeVeiculos>
            )}

            {erroCadastro && <Aviso $tipo="erro" style={{ marginTop: 14 }}><FaExclamationTriangle /> {erroCadastro}</Aviso>}

            <Rodape>
                {/* Saída para quem não quer (ou não consegue) cadastrar agora: o veículo pode ser cadastrado na própria locação. */}
                <Botao type="button" $variante="link" onClick={() => { rastrearTeste('onboarding_frota_pulada'); aoConcluir(); }} disabled={cadastrando}>Pular esta etapa</Botao>
                <Botao type="button" disabled={!podeContinuar} onClick={continuar}>
                    {cadastrando ? 'Cadastrando...' : 'Continuar'} <FaArrowRight />
                </Botao>
            </Rodape>
        </>
    );
}
